const ICON_SIZE = 256;
const LETTER_ICON_SIZE = 64;

/** The name's first character, used when a site has no uploaded icon. */
export const siteIconLetter = (name: string) =>
  (Array.from(name.trim())[0] ?? "H").toLocaleUpperCase();

/** Centre-crops an image to a square PNG, the shape browsers show icons in. */
export async function squareIconFile(file: File): Promise<File> {
  const image = await createImageBitmap(file);
  const side = Math.min(image.width, image.height);
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = Math.min(ICON_SIZE, side);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Your browser couldn’t prepare this image.");
  context.drawImage(
    image,
    (image.width - side) / 2,
    (image.height - side) / 2,
    side,
    side,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  image.close();
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png"),
  );
  if (!blob) throw new Error("Your browser couldn’t prepare this image.");
  return new File([blob], "site-icon.png", { type: "image/png" });
}

/**
 * Draws the letter icon in the colours and heading font of the themed
 * `element`, as a data URL for `<link rel="icon">`.
 */
export async function letterIconUrl(letter: string, element: Element) {
  const style = getComputedStyle(element);
  const read = (name: string) => style.getPropertyValue(name).trim();
  const font = `${read("--site-heading-weight") || 600} 40px ${read("--font-site-heading") || "serif"}`;
  await document.fonts.load(font, letter).catch(() => undefined);
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = LETTER_ICON_SIZE;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.fillStyle = read("--color-site-accent");
  context.beginPath();
  context.roundRect(0, 0, LETTER_ICON_SIZE, LETTER_ICON_SIZE, 14);
  context.fill();
  context.fillStyle = read("--color-site-bg");
  context.font = font;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(letter, LETTER_ICON_SIZE / 2, LETTER_ICON_SIZE / 2 + 2);
  return canvas.toDataURL("image/png");
}

/** Points the page's favicon at `href`, restoring the previous one on cleanup. */
export function setFavicon(href: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  const previous = link ? { href: link.href, type: link.type } : null;
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.append(link);
  }
  link.removeAttribute("type");
  link.href = href;
  const current = link;
  return () => {
    if (previous) {
      current.href = previous.href;
      current.type = previous.type;
    } else current.remove();
  };
}
